import { StudentProxyType } from '../../peekapak-types/DataProtocolTypes';
import { Lesson } from '../../peekapak-types/LessonPlanTypes';
import { AttendanceSession, AttendanceSubmissionPayload } from '../BackendInterface';

export type AttendanceStatus = 'present' | 'absent';

export interface AttendanceDraft {
  shiftNumber: string;
  selectedSessions: AttendanceSession[];
  attendanceStatuses: Record<string, AttendanceStatus>;
}

/**
 * Initialises an attendance status map with every student defaulting to 'present'.
 * The map is keyed by each student's `userId`.
 */
export function initializeAttendanceStatuses(students: StudentProxyType[]): Record<string, AttendanceStatus> {
  return students.reduce<Record<string, AttendanceStatus>>((acc, student) => {
    acc[student.userId] = 'present';
    return acc;
  }, {});
}

/**
 * Counts how many students are present and absent in the given status map.
 */
export function countAttendance(statuses: Record<string, AttendanceStatus>): {
  present: number;
  absent: number;
} {
  return Object.values(statuses).reduce(
    (acc, status) => {
      if (status === 'present') {
        acc.present += 1;
      } else {
        acc.absent += 1;
      }
      return acc;
    },
    { present: 0, absent: 0 },
  );
}

/**
 * Returns the localforage key used to cache an attendance draft for a specific classroom.
 */
export function buildAttendanceCacheKey(classroomId: string): string {
  return `takween-attendance-draft-${classroomId}`;
}

/**
 * Converts a Set of selected lessonIds into AttendanceSession objects using the loaded lesson list.
 * Lessons not found in the list are silently skipped.
 * sessionNumber is idx + 1 in the rendered lesson order.
 */
export function buildSessionObjects(selectedSessions: Set<string>, lessons: Lesson[]): AttendanceSession[] {
  return lessons.reduce<AttendanceSession[]>((acc, lesson, idx) => {
    if (!selectedSessions.has(lesson.lessonId)) {
      return acc;
    }

    acc.push({
      lessonId: lesson.lessonId,
      sessionNumber: idx + 1,
      title: lesson.title,
    });

    return acc;
  }, []);
}

/**
 * Assembles the payload object for the submitAttendance API call.
 */
export function buildSubmissionPayload(
  classroomId: string,
  unitId: string,
  shiftNumber: string,
  selectedSessions: Set<string>,
  attendanceStatuses: Record<string, AttendanceStatus>,
  lessons: Lesson[],
): AttendanceSubmissionPayload {
  return {
    classroomId,
    unitId,
    shiftNumber: parseInt(shiftNumber, 10),
    sessions: buildSessionObjects(selectedSessions, lessons),
    attendanceStatuses,
    submittedAt: Date.now(),
  };
}
